import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form } from "react-bootstrap";
import SelectDropdown from "../components-react/SelectDropdown";
import MultiSelect from "../components-react/MultiSelect";
import {
  recipient_roles,
  custom_types,
  regularPlansDropdown,
  renewal_unpaid,
} from "../common-data/DropdowData";
import {
  fetchAllMembersData,
  fetchClusterManagersData,
  fetchLeadSourceData,
} from "../../redux/reducers/dropdownApiSlice";
import {
  fetchAllLeadsData,
  fetchAllTrainerData,
} from "../../redux/reducers/apiSlice";
import { fetchReceptionistsData } from "../../redux/prospect-reducers/prospectSlice";
import { selectGymData } from "../../redux/reducers/gymDataSlice";
import Loader from "../ui/Loader";
import { ReloadIcon } from "../../images/icons/icons";

const RecipientFilter = ({
  formValues,
  setFormValues,
  formErrors,
  userInfoData,
  onValidationChange,
  config = {},
}) => {
  const dispatch = useDispatch();
  const gymData = useSelector(selectGymData);

  const {
    members,
    loading,
    loading: leadSourcesLoading,
    leadSources,
    clusterManagers,
    clusterManagersLoading,
  } = useSelector((state) => state.dropdown);

  const {
    allLeads,
    allLeadsLoading,
    loading: trainerDataLoading,
    trainerData,
  } = useSelector((state) => state.api);

  const { receptionistsLoading, receptionists } = useSelector(
    (state) => state.prospects
  );

  const [followUpMembers, setFollowUpMembers] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [receptionistData, setReceptionistData] = useState([]);

  // Configuration with defaults
  const {
    showRecipientType = true,
    showMemberType = true,
    showGymFilter = true,
    showLeadSource = true,
    showSubscriptionFilters = true,
    showDateFilters = true,
    showSendToAll = true,
    recipientLabel = "recipient",
    required = true,
  } = config;

  // --------------- GET DATA ---------------
  const getMembersData = useCallback(
    (filters) => {
      dispatch(
        fetchAllMembersData({
          filters: filters,
          token: userInfoData?.token,
        })
      );
    },
    [dispatch, userInfoData?.token]
  );

  const getAllLeads = useCallback(
    (filters) => {
      dispatch(
        fetchAllLeadsData({
          filters: filters,
          token: userInfoData?.token,
        })
      );
    },
    [dispatch, userInfoData?.token]
  );

  const getLeadSources = useCallback(
    (filters) => {
      dispatch(
        fetchLeadSourceData({
          filters: filters,
          token: userInfoData?.token,
        })
      );
    },
    [dispatch, userInfoData?.token]
  );

  const getTrainerData = useCallback(
    (filters) => {
      dispatch(fetchAllTrainerData({ filters, token: userInfoData?.token }));
    },
    [dispatch, userInfoData?.token]
  );

  const getReceptionists = useCallback(
    (filters) => {
      dispatch(
        fetchReceptionistsData({
          filters: filters,
          token: userInfoData?.token,
        })
      );
    },
    [dispatch, userInfoData?.token]
  );

  const getClusterManagers = useCallback(
    (filters) => {
      dispatch(
        fetchClusterManagersData({
          filters: filters,
          token: userInfoData?.token,
        })
      );
    },
    [dispatch, userInfoData?.token]
  );
  // ------------------ END GET DATA ------------------

  const onChangeInput = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "startDate" || name === "endDate") {
      setFormValues((prev) => ({
        ...prev,
        selectedMembers: [],
        selectedTrainers: [],
        selectedReceptionists: [],
        selectedFollowUpMembers: [],
        selectedClusterManagers: [],
        [name]: type === "checkbox" ? checked : value,
      }));
    } else {
      setFormValues((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const onChangeDropdown = (value, name) => {
    if (name === "selectedRecipient") {
      setFormValues((prev) => ({
        ...prev,
        selectedMembers: [],
        selectedTrainers: [],
        selectedReceptionists: [],
        selectedFollowUpMembers: [],
        selectedClusterManagers: [],
        selectedMemberType: [],
        [name]: value,
      }));
    } else if (
      name === "selectedGym" ||
      name === "selectedSubscriptionType" ||
      name === "subscriptionTypeValue" ||
      name === "selectedPlanDuration" ||
      name === "selectedLeadSource" ||
      name === "selectedMemberType"
    ) {
      setFormValues((prev) => ({
        ...prev,
        selectedMembers: [],
        selectedTrainers: [],
        selectedReceptionists: [],
        selectedFollowUpMembers: [],
        selectedClusterManagers: [],
        [name]: value,
      }));
    } else {
      setFormValues((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Validation function
  const validateRecipientFilter = useCallback(
    (values) => {
      const errors = {};

      if (required && showRecipientType && !values.selectedRecipient) {
        errors.selectedRecipient = "Recipient type is required!";
      }

      if (
        showMemberType &&
        values.selectedRecipient?.value === "member" &&
        !values.isAllMembers &&
        !values.selectedMemberType
      ) {
        errors.selectedMemberType = "Member type is required!";
      }

      if (!values.isAllMembers) {
        if (values.selectedRecipient?.value === "member") {
          if (values.selectedMemberType?.value === "lead") {
            if (
              !values.selectedFollowUpMembers ||
              values.selectedFollowUpMembers.length === 0
            ) {
              errors.selectedFollowUpMembers =
                "At least one follow-up member is required!";
            }
          } else if (values.selectedMemberType?.value === "member") {
            if (
              !values.selectedMembers ||
              values.selectedMembers.length === 0
            ) {
              errors.selectedMembers = "At least one member is required!";
            }
          }
        } else if (values.selectedRecipient?.value === "trainer") {
          if (
            !values.selectedTrainers ||
            values.selectedTrainers.length === 0
          ) {
            errors.selectedTrainers = "At least one trainer is required!";
          }
        } else if (values.selectedRecipient?.value === "receptionist") {
          if (
            !values.selectedReceptionists ||
            values.selectedReceptionists.length === 0
          ) {
            errors.selectedReceptionists =
              "At least one receptionist is required!";
          }
        } else if (values.selectedRecipient?.value === "cluster_manager") {
          if (
            !values.selectedClusterManagers ||
            values.selectedClusterManagers.length === 0
          ) {
            errors.selectedClusterManagers =
              "At least one cluster manager is required!";
          }
        }
      }

      return errors;
    },
    [required, showRecipientType, showMemberType]
  );

  // Expose validation to parent
  useEffect(() => {
    if (onValidationChange) {
      const errors = validateRecipientFilter(formValues);
      onValidationChange(errors);
    }
  }, [formValues, onValidationChange, validateRecipientFilter]);

  // Data fetching effects
  useEffect(() => {
    if (formValues.selectedRecipient?.value === "member") {
      const gymIds = formValues.selectedGym?.value || "";
      const planDuration = formValues.selectedPlanDuration?.value || "";
      const subscriptionType = formValues.selectedSubscriptionType?.value || "";
      const daysValue = formValues.subscriptionTypeValue || "";

      const sendableFilters = {
        page: 1,
        limit: 5000,
        search: "",
        gym_id: gymIds,
        plan_duration: planDuration,
        member_type: subscriptionType,
        days_value: daysValue || "",
        subscription_start_date: formValues.startDate || "",
        subscription_end_date: formValues.endDate || "",
        source_name: formValues.selectedLeadSource?.value || "",
      };

      getMembersData(sendableFilters);
    }
  }, [
    formValues.selectedRecipient,
    formValues.selectedGym,
    formValues.selectedSubscriptionType,
    formValues.selectedPlanDuration,
    formValues.subscriptionTypeValue,
    formValues.startDate,
    formValues.endDate,
    formValues.selectedLeadSource,
    getMembersData,
  ]);

  useEffect(() => {
    if (formValues.selectedRecipient?.value === "member") {
      const sendableFilters = {
        limit: 2000,
        page: 1,
        status: "active",
      };

      getLeadSources(sendableFilters);
    }
  }, [formValues.selectedRecipient, getLeadSources]);

  useEffect(() => {
    if (formValues.selectedMemberType?.value === "lead") {
      const sendableLeadFilters = {
        page: 1,
        limit: 5000,
        lead_status: "pending",
        status: "active",
        source_name: formValues.selectedLeadSource?.value || "",
        gym_id: formValues.selectedGym?.value || "",
      };

      getAllLeads(sendableLeadFilters);
    }
  }, [
    formValues.selectedMemberType,
    formValues.selectedGym,
    formValues.selectedLeadSource,
    getAllLeads,
  ]);

  useEffect(() => {
    if (
      formValues.selectedRecipient?.value === "trainer" ||
      formValues.selectedRecipient?.value === "receptionist" ||
      formValues.selectedRecipient?.value === "cluster_manager"
    ) {
      const sendableFilters = {
        page: 1,
        limit: 5000,
        status: "active",
        gym_id: formValues.selectedGym?.value || "",
      };

      if (formValues.selectedRecipient?.value === "trainer") {
        getTrainerData(sendableFilters);
      }

      if (formValues.selectedRecipient?.value === "receptionist") {
        getReceptionists(sendableFilters);
      }

      if (formValues.selectedRecipient?.value === "cluster_manager") {
        getClusterManagers(sendableFilters);
      }
    }
  }, [
    formValues.selectedRecipient,
    formValues.selectedGym,
    getTrainerData,
    getReceptionists,
    getClusterManagers,
  ]);

  // Populate dropdown options from API responses
  useEffect(() => {
    if (!allLeadsLoading && allLeads?.status && allLeads?.data?.length > 0) {
      const dropdownOptions = allLeads?.data.map((item) => ({
        label: `${item.name}/${item.mobile}`,
        value: item.lead_id,
        info: item,
      }));

      setFollowUpMembers(dropdownOptions);
    } else {
      setFollowUpMembers([]);
    }
  }, [allLeadsLoading, allLeads]);

  useEffect(() => {
    if (
      !trainerDataLoading &&
      trainerData?.status &&
      trainerData?.data?.length > 0
    ) {
      const dropdownOptions = trainerData?.data.map((item) => ({
        label: `${item.name}/${item.mobile}`,
        value: item.user_id,
        info: item,
      }));

      setTrainers(dropdownOptions);
    } else {
      setTrainers([]);
    }
  }, [trainerDataLoading, trainerData]);

  useEffect(() => {
    if (
      !receptionistsLoading &&
      receptionists?.status &&
      receptionists?.data?.length > 0
    ) {
      const dropdownOptions = receptionists?.data.map((item) => ({
        label: `${item.name}/${item.mobile}`,
        value: item.user_id,
        info: item,
      }));

      setReceptionistData(dropdownOptions);
    } else {
      setReceptionistData([]);
    }
  }, [receptionistsLoading, receptionists]);

  return (
    <>
      {/* Recipient Filtering Section */}
      <div className="formInputsGrid threeGridSystem">
        {showRecipientType && (
          <div className="formInputBox">
            <label htmlFor="selectedRecipient" className={`formInputLabel`}>
              {recipientLabel} {required && <span className="required">*</span>}
            </label>

            <div
              className={`commonWrapper ${
                formErrors.selectedRecipient ? "formError" : ""
              }`}
            >
              <SelectDropdown
                options={recipient_roles}
                selectedOption={formValues.selectedRecipient}
                setSelectedOption={(selectedValue) =>
                  onChangeDropdown(selectedValue, "selectedRecipient")
                }
                name="Recipient"
              />
            </div>
          </div>
        )}

        {showMemberType &&
          formValues.selectedRecipient?.value === "member" &&
          !formValues.isAllMembers && (
            <>
              <div className="formInputBox">
                <label
                  htmlFor="selectedMemberType"
                  className={`formInputLabel`}
                >
                  member type {required && <span className="required">*</span>}
                </label>

                <div
                  className={`commonWrapper ${
                    formErrors.selectedMemberType ? "formError" : ""
                  }`}
                >
                  <SelectDropdown
                    options={custom_types}
                    selectedOption={formValues.selectedMemberType}
                    setSelectedOption={(selectedValue) =>
                      onChangeDropdown(selectedValue, "selectedMemberType")
                    }
                    name="Member Type"
                  />
                </div>
              </div>
            </>
          )}

        {showGymFilter && formValues.selectedRecipient && (
          <div className={`formInputBox span2`}>
            <label htmlFor="selectedGym" className={`formInputLabel`}>
              Gyms
            </label>

            <div
              className={`commonWrapper ${
                formErrors.selectedGym ? "formError" : ""
              }`}
            >
              <SelectDropdown
                options={gymData?.length > 0 ? gymData : []}
                selectedOption={formValues.selectedGym}
                setSelectedOption={(selectedValue) =>
                  onChangeDropdown(selectedValue, "selectedGym")
                }
                name="Gym"
              />
            </div>
          </div>
        )}

        {showLeadSource && formValues.selectedRecipient?.value === "member" && (
          <div className="formInputBox">
            <label htmlFor="selectedLeadSource" className={`formInputLabel`}>
              lead source
            </label>

            {leadSourcesLoading ? (
              <Loader />
            ) : (
              <div
                className={`commonWrapper ${
                  formErrors.selectedLeadSource ? "formError" : ""
                }`}
              >
                <SelectDropdown
                  options={
                    leadSources?.data?.length > 0 ? leadSources.data : []
                  }
                  selectedOption={formValues.selectedLeadSource}
                  setSelectedOption={(selectedValue) =>
                    onChangeDropdown(selectedValue, "selectedLeadSource")
                  }
                  name="Lead Source"
                />
              </div>
            )}
          </div>
        )}

        {showSubscriptionFilters &&
          formValues.selectedMemberType?.value === "member" && (
            <>
              <div className="formInputBox">
                <label
                  htmlFor="selectedSubscriptionType"
                  className={`formInputLabel`}
                >
                  Subscription Type{" "}
                </label>

                <div
                  className={`commonWrapper ${
                    formErrors.selectedSubscriptionType ? "formError" : ""
                  }`}
                >
                  <SelectDropdown
                    options={renewal_unpaid}
                    selectedOption={formValues.selectedSubscriptionType}
                    setSelectedOption={(selectedValue) =>
                      onChangeDropdown(
                        selectedValue,
                        "selectedSubscriptionType"
                      )
                    }
                    name="Subscription Type"
                  />
                </div>
              </div>

              {formValues.selectedSubscriptionType?.value && (
                <div className="formInputBox">
                  <label
                    htmlFor="subscriptionTypeValue"
                    className={`formInputLabel`}
                  >
                    Subscription Type value{" "}
                  </label>

                  <input
                    type="number"
                    placeholder="Enter subscription type value"
                    className={`formInput ${
                      formErrors?.subscriptionTypeValue ? "formError" : ""
                    }`}
                    name="subscriptionTypeValue"
                    value={formValues.subscriptionTypeValue}
                    onChange={onChangeInput}
                    min={0}
                    max={365}
                  />

                  <span className="tiny-info-label">
                    ⓘ{" "}
                    {formValues.selectedSubscriptionType?.value === "renewal"
                      ? "Renewal Within"
                      : "Unpaid From"}{" "}
                    {formValues.subscriptionTypeValue ||
                      (formValues.selectedSubscriptionType?.value === "renewal"
                        ? "30"
                        : "All")}{" "}
                    Days
                  </span>
                </div>
              )}

              <div className="formInputBox">
                <label
                  htmlFor="selectedPlanDuration"
                  className={`formInputLabel`}
                >
                  plan duration{" "}
                </label>

                <div
                  className={`commonWrapper ${
                    formErrors.selectedPlanDuration ? "formError" : ""
                  }`}
                >
                  <SelectDropdown
                    options={regularPlansDropdown}
                    selectedOption={formValues.selectedPlanDuration}
                    setSelectedOption={(selectedValue) =>
                      onChangeDropdown(selectedValue, "selectedPlanDuration")
                    }
                    name="Plan Duration"
                  />
                </div>
              </div>
            </>
          )}

        {showDateFilters &&
          formValues.selectedMemberType?.value === "member" && (
            <>
              <div className="formInputBox">
                <label htmlFor="startDate" className={`formInputLabel`}>
                  subscription start from{" "}
                  {formErrors.startDate && <span className="required">*</span>}
                </label>

                <input
                  type="date"
                  className={`formInput ${
                    formValues.startDate ? "date-has-value" : ""
                  } ${formErrors?.startDate ? "formError" : ""}`}
                  name="startDate"
                  value={formValues.startDate}
                  onChange={onChangeInput}
                />
              </div>

              <div className="formInputBox">
                <label htmlFor="endDate" className={`formInputLabel`}>
                  subscription end on{" "}
                  {formErrors.endDate && <span className="required">*</span>}
                </label>

                <input
                  type="date"
                  className={`formInput ${
                    formValues.endDate ? "date-has-value" : ""
                  } ${formErrors?.endDate ? "formError" : ""}`}
                  name="endDate"
                  value={formValues.endDate}
                  onChange={onChangeInput}
                />
              </div>
            </>
          )}

        {/* Member Selection */}
        {formValues.selectedMemberType?.value === "member" && (
          <div className={`formInputBox span2`}>
            <label htmlFor="selectedMembers" className={`formInputLabel`}>
              members{" "}
              {formErrors?.selectedMembers && (
                <span className="required">*</span>
              )}
              <span 
                className="broadcast-reload-icon"
                onClick={getMembersData}
                title="Reload members data"
              >
                <ReloadIcon />
              </span>
            </label>

            {loading ? (
              <Loader />
            ) : (
              <div
                className={`commonWrapper no-border multi-select multi-show-option ${
                  formErrors?.selectedMembers ? "error" : ""
                }`}
              >
                <MultiSelect
                  options={members?.length > 0 ? members : []}
                  state={formValues.selectedMembers}
                  setState={(selectedValue) =>
                    onChangeDropdown(selectedValue, "selectedMembers")
                  }
                  name="select multiple members"
                  preventKeyboardOpen={false}
                  isShowList={true}
                />
              </div>
            )}

            <span className="tiny-info-label">
              ⓘ Upto 5000 members can be fetched at a time!
            </span>
          </div>
        )}

        {/* Lead Selection */}
        {formValues.selectedMemberType?.value === "lead" && (
          <>
            <div className={`formInputBox span2`}>
              <label
                htmlFor="selectedFollowUpMembers"
                className={`formInputLabel`}
              >
                follow-up Leads{" "}
                {formErrors?.selectedFollowUpMembers && (
                  <span className="required">*</span>
                )}
              </label>

              {allLeadsLoading ? (
                <Loader />
              ) : (
                <div
                  className={`commonWrapper no-border multi-select multi-show-option ${
                    formErrors?.selectedFollowUpMembers ? "error" : ""
                  }`}
                >
                  <MultiSelect
                    options={followUpMembers}
                    state={formValues.selectedFollowUpMembers}
                    setState={(selectedValue) =>
                      onChangeDropdown(selectedValue, "selectedFollowUpMembers")
                    }
                    name="select multiple follow-up members"
                    preventKeyboardOpen={false}
                    isShowList={true}
                  />
                </div>
              )}

              <span className="tiny-info-label">
                ⓘ Upto 5000 members can be fetched at a time!
              </span>
            </div>
          </>
        )}

        {/* Trainer Selection */}
        {formValues.selectedRecipient?.value === "trainer" &&
          !formValues.isAllMembers && (
            <div className={`formInputBox span2`}>
              <label htmlFor="selectedTrainers" className={`formInputLabel`}>
                trainers{" "}
                {formErrors?.selectedTrainers && (
                  <span className="required">*</span>
                )}
              </label>

              {trainerDataLoading ? (
                <Loader />
              ) : (
                <div
                  className={`commonWrapper no-border multi-select multi-show-option ${
                    formErrors?.selectedTrainers ? "error" : ""
                  }`}
                >
                  <MultiSelect
                    options={trainers}
                    state={formValues.selectedTrainers}
                    setState={(selectedValue) =>
                      onChangeDropdown(selectedValue, "selectedTrainers")
                    }
                    name="select multiple trainers"
                    preventKeyboardOpen={false}
                    isShowList={true}
                  />
                </div>
              )}
            </div>
          )}

        {/* Receptionist Selection */}
        {formValues.selectedRecipient?.value === "receptionist" &&
          !formValues.isAllMembers && (
            <div className={`formInputBox span2`}>
              <label
                htmlFor="selectedReceptionists"
                className={`formInputLabel`}
              >
                receptionists{" "}
                {formErrors?.selectedReceptionists && (
                  <span className="required">*</span>
                )}
              </label>

              {receptionistsLoading ? (
                <Loader />
              ) : (
                <div
                  className={`commonWrapper no-border multi-select multi-show-option ${
                    formErrors?.selectedReceptionists ? "error" : ""
                  }`}
                >
                  <MultiSelect
                    options={receptionistData}
                    state={formValues.selectedReceptionists}
                    setState={(selectedValue) =>
                      onChangeDropdown(selectedValue, "selectedReceptionists")
                    }
                    name="select multiple receptionists"
                    preventKeyboardOpen={false}
                    isShowList={true}
                  />
                </div>
              )}
            </div>
          )}

        {/* Cluster Manager Selection */}
        {formValues.selectedRecipient?.value === "cluster_manager" &&
          !formValues.isAllMembers && (
            <div className={`formInputBox span2`}>
              <label
                htmlFor="selectedClusterManagers"
                className={`formInputLabel`}
              >
                cluster managers{" "}
                {formErrors?.selectedClusterManagers && (
                  <span className="required">*</span>
                )}
              </label>

              {clusterManagersLoading ? (
                <Loader />
              ) : (
                <div
                  className={`commonWrapper no-border multi-select multi-show-option ${
                    formErrors?.selectedClusterManagers ? "error" : ""
                  }`}
                >
                  <MultiSelect
                    options={clusterManagers}
                    state={formValues.selectedClusterManagers}
                    setState={(selectedValue) =>
                      onChangeDropdown(selectedValue, "selectedClusterManagers")
                    }
                    name="select multiple cluster managers"
                    preventKeyboardOpen={false}
                    isShowList={true}
                  />
                </div>
              )}
            </div>
          )}
      </div>

      {/* Send to All Switch */}
      {showSendToAll && (
        <div className="formInputsGrid switchInputs twoGridSystem">
          <div className="formInputBox formInputSwitchBox flex-start">
            <div
              className={`commonWrapper commonSwitchWrapper no-border ${
                formErrors?.isAllMembers ? "formError" : ""
              }`}
            >
              <Form.Check
                type="switch"
                name="isAllMembers"
                label={`Send To All ${
                  formValues.selectedRecipient?.label || "Recipients"
                }`}
                checked={formValues.isAllMembers}
                onChange={onChangeInput}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RecipientFilter;
